/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Chip,
  Table,
  TextField,
  useTableContext,
  TableProvider,
  IconButton,
  Button
} from 'components'
import { formatCurrency, formatDate } from 'utils'
import { useDebounce } from 'utils/hooks'
import { FilterListOrder } from './components/Filter'
import { OrderListProvider } from './context/orderList.context'
import { useLoadOrders } from 'modules/orders/hooks'
import { MoreVert } from '@mui/icons-material'
import { Box, Tooltip } from '@mui/material'
import { FilterListOrderMode } from './components/FilterMode'
import { PageContent } from 'layout'
import { useLayoutContext } from 'layout/layout.context'

import { Actions } from './components/actions'
import { FutureDeliveryModal } from 'modules/orders/components/futureDeliveryModal'
import { useFutureDeliveryModalStore } from 'modules/orders/components/futureDeliveryModal/useFutureDeliveryModalStore'
import { statusColors } from 'modules/orders/utils'
import { useOrderStore } from 'modules/orders/store'
import { HiPlus } from 'react-icons/hi'

const sortFieldMap: any = {
  orderId: 'orderId',
  proposalId: 'proposalId',
  customerName: 'customer_name',
  representantOrderNumber: 'representant_order_number',
  status: 'status_name',
  totalOrder: 'totalOrder',
  createdAt: 'orderDate',
  nf: 'nf',
  sellerName: 'seller_name',
  unity: 'unity_id',
  responsibleId: 'responsibleId'
}

const ReserveChip = ({ isReserve }: { isReserve?: boolean }) => {
  if (!isReserve) return null

  return (
    <Chip
      size="small"
      label="RESERVA"
      color="warning"
      sx={{ fontSize: 10, height: 18 }}
    />
  )
}

const ProposalOrderId = (props: any) => {
  const { row } = props

  if (!row?.value) return <span>-</span>

  if (row?.row?.orderId && row?.value) return row.value

  return (
    <Link
      style={{
        textDecoration: 'none',
        color: '#0C95EB'
      }}
      to={`/orders/web/${row.value}/details`}
    >
      <div style={{ fontWeight: 500 }}>{row.value}</div>
    </Link>
  )
}

const UnityId = (props: any) => {
  const { row } = props

  if (!row?.value) return <span>-</span>

  return (
    <Tooltip title={row?.value?.name}>
      <div
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          justifyItems: 'center'
        }}
      >
        {row?.value?.id || '-'}
      </div>
    </Tooltip>
  )
}

const OrderListPageProvided = () => {
  const navigation = useNavigate()
  const { setBreadcrumbs } = useLayoutContext()
  const { pagination, setPagination } = useTableContext()

  useEffect(() => {
    setBreadcrumbs([{ label: 'Início', to: '/home' }, { label: 'Pedidos' }])
    return () => setBreadcrumbs([])
  }, [])
  const filters = useOrderStore.useFilters()
  const sort = useOrderStore.useSort()
  const search = useOrderStore.useSearch()
  const setSearch = useOrderStore.useSetSearch()
  const setSort = useOrderStore.useSetSort()

  const searchDebounced = useDebounce(search, 250)

  const {
    data: orders,
    isLoading: ordersLoading,
    isFetching: ordersFetching
  } = useLoadOrders({
    ...pagination,
    page: (pagination.page || 0) + 1,
    ...filters,
    search: searchDebounced,
    sort: sort.map((item: any) => ({
      ...item,
      field: sortFieldMap[item.field]
    }))
  })

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [actionRow, setActionRow] = useState<any>(null)

  const handleClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
    setActionRow(row)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popper' : undefined

  const columns: any[] = [
    {
      field: '#',
      headerName: '#',
      width: 50,
      align: 'center',
      headerAlign: 'center',
      renderCell: (row: any) => {
        return (
          <>
            <IconButton
              aria-describedby={id}
              onClick={(e: any) => handleClick(e, row)}
              aria-controls={open ? 'simple-popper' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <MoreVert />
            </IconButton>
          </>
        )
      }
    },
    filters?.listMode !== 'proposals' && {
      field: 'orderId',
      headerName: 'Pedido',
      width: 100,
      renderCell: (row: any) => {
        if (!row?.value) return '-'

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center'
            }}
          >
            <Link
              style={{
                textDecoration: 'none',
                color: '#0C95EB'
              }}
              to={`/orders/${row.value}/details`}
            >
              <div style={{ fontWeight: 500 }}>{row.value}</div>
            </Link>
          </div>
        )
      }
    },
    {
      field: 'proposalId',
      headerName: 'Orçamento',
      width: 125,
      align: 'center',
      headerAlign: 'center',
      renderCell: (row: any) => <ProposalOrderId row={row} />
    },
    {
      field: 'unity',
      headerName: 'Unid.',
      align: 'center',
      width: 70,
      renderCell: (row: any) => <UnityId row={row} />
    },
    {
      field: 'customerName',
      headerName: 'Cliente',
      flex: 1,
      minWidth: 300,
      renderCell: (row: any) => {
        return (
          <div
            style={{
              textDecoration: 'none',
              marginTop: -23,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              justifyItems: 'center',
              color: '#262626'
            }}
          >
            <div style={{ height: 16, color: '#757575' }}>
              {row?.row?.customer?.name || '-'}
            </div>
          </div>
        )
      }
    },
    {
      field: 'representantOrderNumber',
      headerName: 'Nº Ped. Representante',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (row: any) => {
        const value = row?.value?.trim() || '-'

        return (
          <Tooltip title={`Número do Pedido do Representante: ${value}`}>
            <Box
              sx={{
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 13,
                color: '#757575'
              }}
            >
              {value}
            </Box>
          </Tooltip>
        )
      }
    },
    {
      field: 'status',
      headerName: 'Situação',
      width: 200,
      renderCell: (row: any) => {
        if (row?.row?.status?.name) {
          return (
            <Chip
              sx={{
                fontSize: 12,
                width: '100%',
                ...statusColors[row?.row?.status?.id]
              }}
              label={row?.row?.status?.name}
              // color={

              // }
            />
          )
        } else return `-`
      }
    },
    filters?.listMode !== 'futureDelivery' && {
      field: 'reserveOrder',
      headerName: 'Tipo',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (row: any) => {
        const isReserve = !!row?.row?.reserveOrder
        return <ReserveChip isReserve={isReserve} />
      }
    },
    {
      field: 'totalOrder',
      headerName: 'Valor',
      width: 160,
      type: 'number',
      renderCell: (row: any) => {
        return formatCurrency(row.value)
      }
    },
    {
      field: 'createdAt',
      headerName: 'Data',
      width: 120,
      renderCell: (row: any) => {
        return formatDate(row?.row?.orderDate, 'dd/MM/yyyy')
      }
    },
    {
      field: 'sellerName',
      headerName: 'Vendedor',
      flex: 1,
      minWidth: 250,
      renderCell: (row: any) => {
        return (
          <div
            style={{
              textDecoration: 'none',
              marginTop: -23,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              justifyItems: 'center',
              color: '#262626'
            }}
          >
            <div style={{ height: 16, color: '#757575' }}>
              {row.row?.seller?.name || '-'}
            </div>
          </div>
        )
      }
    },
    filters?.listMode === 'proposals' && {
      field: 'responsibleId',
      headerName: 'Quem Aprova',
      width: 200,
      renderCell: (row: any) => {
        let responsibleName = '-'

        if (row.row.responsibleId === 1) {
          responsibleName = 'VENDEDOR'
        } else if (row.row.responsibleId === 2) {
          responsibleName = 'SUPERVISOR'
        } else if (row.row.responsibleId === 3) {
          responsibleName = 'GERENTE'
        } else if (row.row.responsibleId === 4) {
          responsibleName = 'ADMINISTRADOR'
        }

        return responsibleName
      }
    },
    filters?.listMode !== 'proposals' && {
      field: 'nf',
      headerName: 'NF'
    }
  ].filter(Boolean)

  const handleSearch = (event: any) => {
    setSearch(event.target.value.toUpperCase())
    setPagination({ page: 0 })
  }

  return (
    <>
      {open && (
        <Actions
          order={actionRow?.row}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        />
      )}

      <PageContent>
        <Box
          sx={{
            display: { md: 'flex' },
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              marginLeft: { xs: 0, md: 0 },
              marginTop: { xs: 2, md: 0 }
            }}
          >
            <FilterListOrderMode />
          </Box>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <TextField
              placeholder="Pesquisar ..."
              style={{ width: 300, maxWidth: 400 }}
              onChange={handleSearch}
              value={search}
              type="search"
            />
            <div style={{ marginLeft: 16 }}>
              <FilterListOrder />
            </div>
            <Button
              sx={{
                display: { md: 'flex', sm: 'none', xs: 'none' }
              }}
              startIcon={<HiPlus size={18} />}
              onClick={() => {
                navigation('create')
              }}
            >
              Novo Orçamento
            </Button>
          </div>
        </Box>
        <div style={{ marginTop: '1rem', height: 'calc(100vh - 152px)' }}>
          <Table
            rowCount={orders?.meta.query.totalItems}
            columns={columns}
            rows={orders?.data || []}
            loading={ordersLoading || ordersFetching}
            sortModel={sort}
            onSortModelChange={(newSortModel) => setSort(newSortModel)}
          />
        </div>

        <Box
          sx={{
            margin: 0,
            top: 'auto',
            right: 20,
            bottom: 20,
            left: 'auto',
            position: 'fixed',
            display: { md: 'none' }
          }}
        >
          {/* <Fab
            style={{ background: '#023770' }}
            onClick={() => navigation('create')}
          >
            <HiPlus size={24} color="white" />
          </Fab> */}
        </Box>
      </PageContent>
    </>
  )
}

export const OrderListPage = () => {
  const isFutureDeliveryOpen = useFutureDeliveryModalStore.useIsModalOpen()
  return (
    <>
      <OrderListProvider>
        <TableProvider>
          <OrderListPageProvided />
        </TableProvider>
      </OrderListProvider>
      {isFutureDeliveryOpen && <FutureDeliveryModal />}
    </>
  )
}
export default OrderListPage
